import { BUSY_CLASS, PROGRESS_ATTR, SHARE_SELECTOR } from './const';
import { createBrowserShare, supportsFileShare } from './browser';
import { fileNameFromUrl, isIos, shareVideo, TShareOutcome } from './share';

// Progressive enhancement for the generation-result download buttons (§13 / rule 18).
// On iOS a plain link to an .mp4 opens the OS video player instead of downloading;
// Web Share with files lets the user Save to Photos or send to a messenger directly.
// Gated to iOS on purpose (see isIos) — Android/desktop download the link fine, so we
// leave their working flow untouched.
// We hook whichever button the user tapped (FullHD or mobile — share the version they
// clicked). Where Web Share files aren't supported (old iOS, desktop) we do nothing
// and the browser follows the href exactly as before.

const cap = createBrowserShare();

function setBusy(el: HTMLElement, ratio: number | null): void {
    el.classList.add(BUSY_CLASS);
    el.setAttribute(
        PROGRESS_ATTR,
        ratio == null ? '…' : `${Math.round(ratio * 100)}%`
    );
}

function clearBusy(el: HTMLElement): void {
    el.classList.remove(BUSY_CLASS);
    el.removeAttribute(PROGRESS_ATTR);
}

async function handle(link: HTMLAnchorElement): Promise<void> {
    if (link.classList.contains(BUSY_CLASS)) {
        return; // re-tap guard
    }
    const url = link.href;
    const name = link.dataset.shareName || fileNameFromUrl(url);

    setBusy(link, null);
    let outcome: TShareOutcome;
    try {
        outcome = await shareVideo(url, name, cap, (ratio) => setBusy(link, ratio));
    } finally {
        clearBusy(link);
    }

    // 'shared'/'aborted' stay put; only a hard failure or unsupported share falls
    // back to the native download (open the link we intercepted).
    if (outcome === 'unsupported' || outcome === 'error') {
        window.location.href = url;
    }
}

function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement | null;
    const link = target?.closest<HTMLAnchorElement>(SHARE_SELECTOR);
    if (!link) {
        return;
    }
    // Only intercept on iOS where we can actually improve things; otherwise let the
    // browser download/open the link natively.
    if (!isIos(navigator) || !supportsFileShare()) {
        return;
    }
    e.preventDefault();
    void handle(link);
}

// Event delegation: the result buttons are injected by the AJAX status poller
// (GenerationRequestLive), so they may not exist at load time.
document.addEventListener('click', onClick);
